import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { aciesDateFormat } from '@shared/helpers';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { SyncStatusTranslatePipe } from '@shared/pipes/sync-status-translate.pipe';
import { NotificationService } from '@shared/services/notification.service';
import { IAccount, UserService } from '@shared/services/user.service';
import { AuthService } from '../../auth/auth.service';
import { serviceItems } from '../../topbar/servicemenu';
import { UserPermissionsService } from '@shared/services/userpermissions.service';
import { actionDefintion } from '@shared/utils/SimpleActionManager';
import { catchError, filter, finalize, forkJoin, interval, map, of, Subject, take, takeUntil } from 'rxjs';
import { ISourceDriftDialogData, SourceDriftDialogComponent } from './source-drift-dialog/source-drift-dialog.component';
import { AuditReportDialogComponent, IAuditReportDialogData } from './audit-report-dialog/audit-report-dialog.component';
import { CreateWorkerDialogComponent, ICreateWorkerDialogData } from './create-worker-dialog/create-worker-dialog.component';
import { DriftAuditDialogComponent, IDriftAuditDialogData } from './drift-audit-dialog/drift-audit-dialog.component';
import { IRepairEnvironmentDialogData, RepairEnvironmentDialogComponent } from './repair-environment-dialog/repair-environment-dialog.component';
import { IResetSyncDialogData, ResetSyncDialogComponent } from './reset-sync-dialog/reset-sync-dialog.component';
import { auditStatusOf, findEngineTarget, formatUptime, hasEngineTable, IAuditTotals, isAuditIssue, isHeartbeatRestart, isNotInEngine, sumAuditTotals } from './sync-monitoring-audit.utils';
import {
	IEnvironmentAudit,
	IEnvironmentDetail,
	IEnvironmentRepairNeeded,
	IEnvironmentStatusResponse,
	IEnvironmentSummary,
	IHeartbeatModel,
	IHeartbeatRow,
	IStatusTableModel,
	ITableErrorDisplay,
	ITableErrorModel,
	ITableRow,
	ITransitionDisplay,
	ITransitionModel,
	IWorkerDisplay,
	IWorkerModel,
} from './sync-monitoring.models';
import { formatEpoch } from './sync-monitoring-format';
import { SyncMonitoringService } from './sync-monitoring.service';
import {
	buildRepairNeededTables,
	decideOneMonitorLink,
	deriveTableStatus,
	extractTableName,
	extractWorkerId,
	formatDelay,
	formatStaleDuration,
	formatWorkerConfiguration,
	getStatusSortPriority,
	getWorkerSortPriority,
	hasTargetRowSnapshot,
	isHeartbeatStale,
	isRepairRecommended,
	isFoundNotRepaired,
	isTargetSynchronized,
	IOneMonitorLinkParams,
	IOneMonitorLinkStep,
	IStaleDurationUnitLabels,
	sourceDriftBadge,
	sourceDriftKey,
	truncateSyncError as truncateSyncErrorUtil,
} from './sync-monitoring.utils';

const SUPERADMIN_PERMISSION = 'iam:OrganizationsPut';
// core-one's ManualCleanup policy: drift audits, replays of skipped items, keeping marked items, resetting repair attempts.
const MANUAL_CLEANUP_PERMISSION = 'one:ManualCleanup';
const TRANSLATION_NS = 'administration.sync-monitoring';
const DELAY_HIGH_THRESHOLD_SECONDS = 300;
const TRANSITIONS_LIMIT = 20;
const HIGHLIGHT_CLEAR_MS = 4000;
const AUTO_REFRESH_MS = 60000;

/** The Tables tab filter chips: all, audit issues, in Core Engine (synchronized there), not in Core Engine. */
type TableFilter = 'all' | 'audit' | 'in-engine' | 'engine';

@Component({
	selector: 'app-sync-monitoring',
	templateUrl: './sync-monitoring.component.html',
	styleUrls: ['./sync-monitoring.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class SyncMonitoringComponent implements OnInit, OnDestroy {
	// Deactivation goes through a confirm dialog; the toggle's own click already flips its native
	// checked state, and Angular's one-way `[checked]` binding won't re-push an unchanged value on
	// cancel, so a cancelled confirm needs this reference to force the toggle back explicitly.
	@ViewChild(MatSlideToggle) private syncToggle?: MatSlideToggle;

	private destroy$ = new Subject<void>();
	private currentAccountId: string | null = null;
	private reloadGeneration = 0;
	private errorsGeneration = 0;
	private tableErrorsRequestId = 0;
	/** True once the default-environment call has resolved (success or failure) this init cycle. */
	private defaultEnvironmentSettled = false;
	/** A deep link's query parameters until they are applied or refused. */
	private link: IOneMonitorLinkParams | null = null;
	/** The deep link's environment once accepted; it wins over the default-environment response. */
	private linkedEnvironmentId: string | null = null;

	// Loading / view state
	public isLoading = true;
	public isTableLoading = false;
	public isTransitionsLoading = false;
	public isTogglingSync = false;
	public activeView: 'tables' | 'audit' | 'errors' | 'transitions' = 'tables';
	public hasSuperadminPermission = false;
	public canManageAudit = false;
	public statusLastUpdatedAt: Date | null = null;

	// Auto-refresh countdown (R13) — ticks down once per second; reloadEnvironment() resets it
	// whenever a reload actually starts (manual refresh, auto-refresh, environment switch, or a
	// reset-triggered reload), so the displayed countdown always reflects time since the last load.
	// While paused the countdown freezes in place (rather than resetting) so resuming continues
	// from where it left off instead of granting a full fresh minute.
	private autoRefreshRemainingMs = AUTO_REFRESH_MS;
	public nextRefreshInSeconds = AUTO_REFRESH_MS / 1000;
	public isAutoRefreshPaused = false;

	// Environment selection
	public environments: IEnvironmentSummary[] = [];
	public environmentOptions: { id: string; name: string }[] = [];
	public selectedEnvironment: IEnvironmentDetail | null = null;
	public selectedEnvironmentId: string | null = null;

	// Display collections
	public tableSource = new MatTableDataSource<ITableRow>();
	public workers: IWorkerDisplay[] = [];
	public errors: ITableErrorDisplay[] = [];
	public transitions: ITransitionDisplay[] = [];
	public expandedRow: ITableRow | null = null;
	public highlightedTableSelf: string | null = null;

	// Drift audit: the last status response, its totals, the Tables tab filter and the worker heartbeat histories
	public status: IEnvironmentStatusResponse | null = null;
	public auditTotals: IAuditTotals | null = null;
	public tableFilter: TableFilter = 'all';
	public auditFocus: { tableName: string } | null = null;
	public heartbeatHistory = new Map<string, IHeartbeatRow[] | 'loading' | 'failed'>();

	// Per-table errors for the currently expanded row (single-expand)
	public expandedTableErrors: ITableErrorModel[] = [];
	public expandedTableErrorsLoading = false;
	public expandedTableErrorsError: string | null = null;

	// Section-level error states (partial-failure resilience — R15)
	public workersError: string | null = null;
	public errorsError: string | null = null;
	public transitionsError: string | null = null;
	public selectorDisabled = false;

	public tableColumns: GenericTableColumn[] = [
		{
			name: `${TRANSLATION_NS}.tables.columns.name`,
			dataKey: 'tableName',
			isSortable: true,
		},
		// dataKey stays the raw status slug (sorting + statusClass()/rowClassFn rely on it); pipe
		// only affects the displayed text, translating the slug for the current language.
		{
			name: `${TRANSLATION_NS}.tables.columns.status`,
			dataKey: 'status',
			isSortable: true,
			pipe: SyncStatusTranslatePipe,
		},
		{
			name: `${TRANSLATION_NS}.tables.columns.engine`,
			dataKey: 'engineLabel',
			isSortable: true,
		},
		{
			name: `${TRANSLATION_NS}.tables.columns.audit`,
			dataKey: 'auditLabel',
			isSortable: true,
		},
		{
			name: `${TRANSLATION_NS}.tables.columns.source-drift`,
			dataKey: 'sourceDriftLabel',
			isSortable: true,
			sortKey: 'sourceDriftDifference',
		},
		{
			name: `${TRANSLATION_NS}.tables.columns.row-count`,
			dataKey: 'rowCount',
			isSortable: true,
		},
		{
			name: `${TRANSLATION_NS}.tables.columns.engine-row-count`,
			dataKey: 'engineRowCount',
			isSortable: true,
		},
		{
			name: `${TRANSLATION_NS}.tables.columns.max-row-version`,
			dataKey: 'maxRowVersionHex',
			isSortable: true,
		},
	];

	// Row actions: "Show audit findings" for everyone, "Audit this table" with one:ManualCleanup and
	// Reset Sync for superadmins. Each action's filter hides it on rows it does not apply to.
	public rowActions: actionDefintion<ITableRow>[] = [];

	constructor(
		private syncMonitoringService: SyncMonitoringService,
		private userService: UserService,
		private userPermissionsService: UserPermissionsService,
		private notificationService: NotificationService,
		private dialog: MatDialog,
		private translateService: TranslateService,
		private route: ActivatedRoute,
		private router: Router,
		private authService: AuthService,
	) {
		// The max-row-version column DISPLAYS a formatted hex string (`maxRowVersionHex`), but sorting
		// that string lexicographically is wrong (e.g. "0x10" sorts before "0xF"). Sort by the
		// underlying numeric `maxRowVersion` for that column instead so the order matches magnitude;
		// null (no version) sorts lowest. Other columns keep MatTableDataSource's default behaviour.
		// The filter chips (all / audit issues / not in Core Engine) filter the rows by these flags.
		this.tableSource.filterPredicate = (row: ITableRow, filter: string): boolean => {
			switch (filter as TableFilter) {
				case 'audit':
					return row.auditIssue;
				case 'in-engine':
					return !row.notInEngine;
				case 'engine':
					return row.notInEngine;
				default:
					return true;
			}
		};
		this.tableSource.sortingDataAccessor = (row: ITableRow, columnId: string): string | number => {
			if (columnId === 'maxRowVersionHex') {
				return row.maxRowVersion ?? Number.NEGATIVE_INFINITY;
			}
			const value = (row as unknown as Record<string, unknown>)[columnId];
			return typeof value === 'number' ? value : value == null ? '' : String(value);
		};
	}

	ngOnInit(): void {
		this.hasSuperadminPermission = this.userPermissionsService.hasPermissions([SUPERADMIN_PERMISSION]);
		this.canManageAudit = this.userPermissionsService.hasPermissions([MANUAL_CLEANUP_PERMISSION]);
		const audited = (row: ITableRow): boolean => {
			const table = this.findStatusTable(row.tableName);
			return !!table && hasEngineTable(table);
		};
		this.rowActions = [
			{ title: `${TRANSLATION_NS}.audit.audit-table`, handler: 'audit', filter: (row) => this.canManageAudit && audited(row) },
			{ title: `${TRANSLATION_NS}.audit.show-findings`, handler: 'findings' },
			{ title: `${TRANSLATION_NS}.reset.row-action`, handler: 'reset', filter: () => this.hasSuperadminPermission },
		];

		const params = this.route.snapshot.queryParamMap;
		this.link = { accountId: params.get('accountId'), environmentId: params.get('environmentId'), table: params.get('table'), kind: params.get('kind') };
		this.applyLink(null, null);
		this.initializeForAccount(this.userService.account);

		// The table rows' labels (Core Engine, Audit) are translated when the rows are built, so
		// build them again in the new language.
		this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.status) {
				this.tableSource.data = this.mapTableRows(this.status.tables);
			}
		});

		// Auto-refresh (R13): ticks every second to drive the visible countdown; refresh() already
		// no-ops without a selected environment or while a load is in flight, so calling it
		// unconditionally when the countdown reaches zero is safe. If refresh() no-ops, the
		// countdown wasn't reset by reloadEnvironment(), so reset it here too — otherwise it would
		// stay stuck at zero, calling refresh() every tick until an environment is selected.
		interval(1000)
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				if (this.isAutoRefreshPaused) {
					return;
				}
				this.autoRefreshRemainingMs -= 1000;
				if (this.autoRefreshRemainingMs <= 0) {
					this.refresh();
					if (this.autoRefreshRemainingMs <= 0) {
						this.autoRefreshRemainingMs = AUTO_REFRESH_MS;
					}
				}
				this.nextRefreshInSeconds = Math.max(0, Math.ceil(this.autoRefreshRemainingMs / 1000));
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/** Clears all state and (re)loads the default environment + environments list for the active account. */
	private initializeForAccount(account: IAccount | null): void {
		this.resetState();
		this.currentAccountId = account?.id ?? null;

		if (!account) {
			this.isLoading = false;
			return;
		}

		this.isLoading = true;
		const accountId = this.currentAccountId;

		// Independent requests, not forkJoin'd: the environments list only feeds the selector
		// dropdown, so a slow/hanging /one/environments call must not block the core monitoring
		// path (default environment -> status) from loading (R2/R15).
		this.syncMonitoringService
			.getDefaultEnvironment()
			.pipe(
				takeUntil(this.destroy$),
				catchError((error: unknown) => {
					if (this.isCurrentAccount(accountId)) {
						// Log the real error (HTTP status/parse issue) — the notify() below only shows a
						// translated toast, so without this a /one/environment failure is invisible.
						console.error('[SyncMonitoring] Failed to load the default environment (GET /one/environment)', error);
						this.notify('errors.default-environment-load-failed');
					}
					return of(null);
				}),
			)
			.subscribe((defaultEnv) => {
				if (!this.isCurrentAccount(accountId)) {
					return;
				}
				this.defaultEnvironmentSettled = true;

				if (defaultEnv && !this.linkedEnvironmentId) {
					this.selectedEnvironment = defaultEnv;
					this.selectedEnvironmentId = this.extractId(defaultEnv.self);
					// Seed the selector with the default immediately so mat-select has a matching
					// option to display the moment `selectedEnvironmentId` is set — regardless of
					// whether the (independent, possibly slower) environments-list call has resolved
					// yet, and even if that list omits the default. Without this the default renders
					// as "unselected" because there is no option whose value equals selectedEnvironmentId.
					this.upsertEnvironmentOption(this.selectedEnvironmentId, defaultEnv.name);
					this.loadEnvironmentData(this.selectedEnvironmentId);
				} else if (!this.selectedEnvironmentId && this.environmentOptions.length > 0) {
					// Default-environment endpoint unavailable but the environments list is here:
					// fall back to the first environment so the page is usable instead of blank.
					this.selectEnvironment(this.environmentOptions[0].id);
				} else if (!this.selectedEnvironmentId) {
					// No default and no list yet — the environments-list handler will pick the first
					// environment once it arrives. Stop the initial spinner in the meantime.
					this.isLoading = false;
				}
			});

		this.syncMonitoringService
			.getEnvironments()
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => {
					if (this.isCurrentAccount(accountId)) {
						this.selectorDisabled = true;
						this.notify('errors.environments-load-failed');
					}
					return of([] as IEnvironmentSummary[]);
				}),
			)
			.subscribe((environments) => {
				if (!this.isCurrentAccount(accountId)) {
					return;
				}
				this.environments = environments;
				const options = environments.map((env) => ({
					id: this.extractId(env.self),
					name: env.name,
				}));
				// Keep the current default selection selectable even if the list doesn't include it,
				// so switching to the real list never drops the pre-selected default.
				if (this.selectedEnvironmentId && !options.some((option) => option.id === this.selectedEnvironmentId)) {
					options.unshift({
						id: this.selectedEnvironmentId,
						name: this.selectedEnvironment?.name ?? this.selectedEnvironmentId,
					});
				}
				this.environmentOptions = options;
				this.mergeSelectedEnvironmentFromList();

				const linkStep = this.applyLink(
					options.map((option) => option.id),
					null,
				);
				if (linkStep?.environmentId) {
					this.linkedEnvironmentId = linkStep.environmentId;
					this.onEnvironmentChange(linkStep.environmentId);
					this.applyLinkTable();
				}

				// If the default-environment call already settled without selecting anything (it failed
				// or returned nothing), fall back to the first environment now that the list is here —
				// so the page loads a real environment instead of staying blank.
				if (this.defaultEnvironmentSettled && !this.selectedEnvironmentId && options.length > 0) {
					this.selectEnvironment(options[0].id);
				}
			});
	}

	/** Applies the deep link as far as what has loaded allows, and removes its parameters from the URL once it is handled. */
	private applyLink(environmentIds: readonly string[] | null, tableSelves: readonly string[] | null): IOneMonitorLinkStep | null {
		const link = this.link;
		const step = link ? decideOneMonitorLink(link, this.userService.account?.id ?? null, environmentIds, tableSelves) : null;
		if (!step) {
			this.link = null;
			return null;
		}
		if (step.switchToAccount) {
			this.link = null;
			this.switchToLinkAccount(step.switchToAccount, link);
			return step;
		}
		if (step.notice) {
			this.notificationService.notify([`${TRANSLATION_NS}.link.${step.notice}`, link], [], 'neutral');
		}
		if (step.tableSelf) {
			this.navigateToTable(step.tableSelf, link.kind ?? '');
		}
		if (step.done) {
			this.link = null;
			this.clearLinkFromUrl();
		}
		return step;
	}

	/**
	 * Switches to the link's account once the user's accounts have loaded, then loads that account here and applies the link again. It does not wait for the app to re-create the page: a switch from another account may or may not reload the route (the reload and `selectAccount`'s replay of the stored URL race), and a
	 * first selection reloads nothing. The stored URL keeps `selectAccount` on this page. An account the user cannot open drops the link.
	 */
	private switchToLinkAccount(accountId: string, link: IOneMonitorLinkParams): void {
		this.userService.accounts$
			.pipe(
				filter((accounts) => accounts.length > 0),
				take(1),
				takeUntil(this.destroy$),
			)
			.subscribe((accounts) => {
				const account = accounts.find((entry) => entry.id === accountId);
				if (!account) {
					this.clearLinkFromUrl();
					return;
				}
				const query = new URLSearchParams(Object.entries(link).filter((entry): entry is [string, string] => !!entry[1])).toString();
				localStorage.setItem('User.DeepLink', `administration/sync-monitoring?${query}`);
				this.authService.selectAccount(account, serviceItems);
				this.link = link;
				this.initializeForAccount(account);
			});
	}

	private clearLinkFromUrl(): void {
		void this.router.navigate([], { relativeTo: this.route, queryParams: { accountId: null, environmentId: null, table: null, kind: null }, queryParamsHandling: 'merge', replaceUrl: true });
	}

	/** Applies the deep link's table once the linked environment's table status has loaded. */
	private applyLinkTable(): void {
		if (this.link && this.status && this.selectedEnvironmentId === this.linkedEnvironmentId) {
			this.applyLink(
				this.environmentOptions.map((option) => option.id),
				this.status.tables.map((table) => table.self),
			);
		}
	}

	/** Adds an environment option to the selector if it isn't already present (keeps the default selectable). */
	private upsertEnvironmentOption(id: string, name: string): void {
		if (id && !this.environmentOptions.some((option) => option.id === id)) {
			this.environmentOptions = [{ id, name }, ...this.environmentOptions];
		}
	}

	/** Selects an environment by id and loads its overview + sections (used for the fallback selection). */
	private selectEnvironment(environmentId: string): void {
		this.selectedEnvironmentId = environmentId;
		this.reloadEnvironment(environmentId);
	}

	/** Selector change — reloads the overview + per-section data for the chosen environment. */
	onEnvironmentChange(environmentId: string): void {
		if (!environmentId || environmentId === this.selectedEnvironmentId) {
			return;
		}
		this.selectedEnvironmentId = environmentId;
		// Clear the previous environment's rows and overview immediately (not just once the new
		// environment's data arrives) — otherwise stale data stays visible (and rows clickable for
		// Reset Sync) while selectedEnvironmentId already points at the new environment. Without
		// clearing selectedEnvironment specifically, a failed getEnvironment() for the new
		// environment would leave the OLD environment's name showing in the overview (and in the
		// reset dialog) while selectedEnvironmentId — and any reset request — already targets the
		// new one. A plain refresh() of the SAME environment intentionally does NOT clear any of
		// this (R13 — retain previous data on failure).
		this.tableSource.data = [];
		this.workers = [];
		this.errors = [];
		this.transitions = [];
		this.status = null;
		this.auditTotals = null;
		this.heartbeatHistory.clear();
		this.collapseExpandedRow();
		this.selectedEnvironment = null;
		this.statusLastUpdatedAt = null;
		this.reloadEnvironment(environmentId);
	}

	/** Manual refresh — re-fetches the overview + status + workers + transitions for the current environment (R13). */
	refresh(): void {
		if (!this.selectedEnvironmentId || this.isTableLoading) {
			return;
		}
		this.reloadEnvironment(this.selectedEnvironmentId);
	}

	/** Auto-refresh toggle handler: `enabled` is the NEW desired state (checked = auto-refresh running). */
	onToggleAutoRefresh(enabled: boolean): void {
		this.isAutoRefreshPaused = !enabled;
	}

	private reloadEnvironment(environmentId: string): void {
		// A reload is starting now (manual refresh, auto-refresh, environment switch, or a
		// reset-triggered reload) — restart the auto-refresh countdown from here.
		this.autoRefreshRemainingMs = AUTO_REFRESH_MS;
		this.nextRefreshInSeconds = AUTO_REFRESH_MS / 1000;

		// Shared across getEnvironment() and loadEnvironmentData() below so that a second refresh
		// (or reset-triggered reload) for the SAME environment invalidates the first's responses —
		// isCurrentEnvironment() alone can't detect two overlapping loads of the same environment.
		const generation = ++this.reloadGeneration;
		const isCurrentLoad = (): boolean => generation === this.reloadGeneration && this.isCurrentEnvironment(environmentId);

		this.syncMonitoringService
			.getEnvironment(environmentId)
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => {
					if (isCurrentLoad()) {
						this.notify('errors.environment-load-failed');
					}
					return of(null);
				}),
			)
			.subscribe((environment) => {
				if (environment && isCurrentLoad()) {
					this.selectedEnvironment = {
						...(this.findEnvironmentFromList(environmentId) ?? {}),
						...environment,
					};
				}
			});

		this.loadEnvironmentData(environmentId, generation);
	}

	setActiveView(view: 'tables' | 'audit' | 'errors' | 'transitions'): void {
		this.activeView = view;
		this.auditFocus = null;
		if (view === 'errors' && this.selectedEnvironmentId) {
			this.loadErrors(this.selectedEnvironmentId);
		}
	}

	get statusLastUpdatedDisplay(): string {
		return this.statusLastUpdatedAt ? aciesDateFormat(this.statusLastUpdatedAt, true, true) : '';
	}

	get isSyncDisabled(): boolean {
		return this.selectedEnvironment?.synchronization?.disabled === true;
	}

	// --- Synchronization activation toggle (superadmin only) -------------------

	/** Slide-toggle handler: `enabled` is the NEW desired state (checked = synchronization active). */
	onToggleSynchronization(enabled: boolean): void {
		if (!this.selectedEnvironment || !this.selectedEnvironmentId || this.isTogglingSync) {
			return;
		}
		const disabled = !enabled;

		// Deactivating stops synchronization for the environment — confirm first. Activating is direct.
		if (disabled) {
			this.dialog
				.open(ConfirmdialogComponent, {
					width: '440px',
					data: {
						heading: this.translateService.instant(`${TRANSLATION_NS}.sync-toggle.deactivate-title`),
						content: this.translateService.instant(`${TRANSLATION_NS}.sync-toggle.deactivate-content`),
						okButtonText: this.translateService.instant(`${TRANSLATION_NS}.sync-toggle.deactivate-confirm`),
						cancelButtonText: this.translateService.instant('general.cancel'),
					},
				})
				.afterClosed()
				.pipe(takeUntil(this.destroy$))
				.subscribe((confirmed) => {
					if (confirmed) {
						this.applySynchronizationDisabled(disabled);
					} else if (this.syncToggle) {
						// isSyncDisabled is unchanged, but the toggle's click already flipped its own
						// checked state; the `[checked]` binding won't re-push that unchanged value, so
						// reset the toggle directly instead of leaving it visually (but not actually) off.
						this.syncToggle.checked = !this.isSyncDisabled;
					}
				});
		} else {
			this.applySynchronizationDisabled(disabled);
		}
	}

	private applySynchronizationDisabled(disabled: boolean): void {
		const environment = this.selectedEnvironment;
		const environmentId = this.selectedEnvironmentId;
		if (!environment || !environmentId) {
			return;
		}
		this.isTogglingSync = true;
		this.syncMonitoringService
			.setSynchronizationDisabled(environment, disabled)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.isTogglingSync = false;
				}),
			)
			.subscribe({
				next: () => {
					this.notificationService.notify([`${TRANSLATION_NS}.sync-toggle.${disabled ? 'deactivated' : 'activated'}`], [], 'success');
					// Re-fetch so the overview + status reflect the new synchronization state.
					if (this.isCurrentEnvironment(environmentId)) {
						this.reloadEnvironment(environmentId);
					}
				},
				error: () => this.notify('errors.sync-toggle-failed'),
			});
	}

	// --- Drift audit (one:ManualCleanup) ---------------------------------------

	/** The environment's drift audit summary from the last status response. */
	get envAudit(): IEnvironmentAudit | null {
		return this.status?.audit ?? null;
	}

	/** Tables that have an engine table and so can be audited. */
	get auditableTables(): string[] {
		return (this.status?.tables ?? []).filter(hasEngineTable).map((table) => extractTableName(table.self));
	}

	/** When the newest audit run of any table completed, whatever started it (a full pass, a nightly re-check or a manual audit). */
	get latestAuditAt(): number | null {
		const completed = (this.status?.tables ?? []).map((table) => table.audit?.completedAt ?? 0);
		const latest = Math.max(0, ...completed);
		return latest > 0 ? latest : null;
	}

	get differingTableCount(): number {
		return (this.status?.tables ?? []).filter((table) => auditStatusOf(table.audit) === 'differing').length;
	}

	get auditIssueCount(): number {
		return this.tableSource.data.filter((row) => row.auditIssue).length;
	}

	get inEngineCount(): number {
		return this.tableSource.data.length - this.notInEngineCount;
	}

	get notInEngineCount(): number {
		return this.tableSource.data.filter((row) => row.notInEngine).length;
	}

	/** Tables whose repair gave up on items after 3 attempts, with how many. */
	get exhaustedTables(): { tableName: string; count: number }[] {
		return (this.status?.tables ?? []).filter((table) => (table.audit?.exhaustedCount ?? 0) > 0).map((table) => ({ tableName: extractTableName(table.self), count: table.audit?.exhaustedCount ?? 0 }));
	}

	/** Opens the drift audit dialog for all tables, or with `tables` preselected; refreshes once the audit is dispatched. */
	openDriftAuditDialog(tables: string[] = []): void {
		if (!this.canManageAudit || !this.selectedEnvironment || !this.selectedEnvironmentId) {
			return;
		}
		const data: IDriftAuditDialogData = {
			environmentId: this.selectedEnvironmentId,
			environmentName: this.selectedEnvironment.name,
			tables: this.auditableTables,
			selected: tables,
		};
		this.dialog
			.open(DriftAuditDialogComponent, { data, width: '640px' })
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => {
				if (response && this.selectedEnvironmentId) {
					const dispatched = response.tables ?? [];
					const running = dispatched.filter((table) => table.status === 'in_progress').length;
					this.notificationService.notify([`${TRANSLATION_NS}.audit.requested`], [`${TRANSLATION_NS}.audit.requested-text`, { dispatched: dispatched.length - running, running }], 'success');
					this.loadEnvironmentData(this.selectedEnvironmentId);
				}
			});
	}

	/** Opens the audit report dialog, which waits while core-one builds the report and then shows, copies and saves it. */
	openReportDialog(): void {
		if (!this.selectedEnvironment || !this.selectedEnvironmentId) {
			return;
		}
		const data: IAuditReportDialogData = { environmentId: this.selectedEnvironmentId, environmentName: this.selectedEnvironment.name };
		this.dialog.open(AuditReportDialogComponent, { data, width: '760px' });
	}

	/** Opens the source drift dialog for a table; a started range check reloads the environment data behind it. */
	openSourceDriftDialog(tableName: string): void {
		if (!this.selectedEnvironmentId) {
			return;
		}
		const data: ISourceDriftDialogData = { environmentId: this.selectedEnvironmentId, tableName, canManage: this.canManageAudit };
		const ref = this.dialog.open(SourceDriftDialogComponent, { data, width: '760px' });
		ref.componentInstance.started.pipe(takeUntil(this.destroy$), takeUntil(ref.afterClosed())).subscribe(() => this.selectedEnvironmentId && this.loadEnvironmentData(this.selectedEnvironmentId));
	}

	/** Switches to the Audit tab with the table's findings open. */
	showAuditFindings(tableName: string): void {
		this.activeView = 'audit';
		this.auditFocus = { tableName };
	}

	/** Reset Sync for one table, asked for from the Audit tab. */
	openResetDialogForTable(tableName: string): void {
		const row = this.tableSource.data.find((candidate) => candidate.tableName === tableName);
		if (row) {
			this.openResetDialog(row);
		}
	}

	/** Filter chip handler for the Tables tab. */
	setTableFilter(filter: TableFilter): void {
		this.tableFilter = filter;
		this.tableSource.filter = filter;
	}

	private findStatusTable(tableName: string): IStatusTableModel | null {
		return this.status?.tables.find((table) => extractTableName(table.self) === tableName) ?? null;
	}

	// --- Reset actions (task .16, superadmin only) -----------------------------

	/** Opens the reset dialog with one table (row) preselected, or on all tables (null); refreshes status on success. */
	openResetDialog(row: ITableRow | null): void {
		this.openResetDialogFor(row ? [{ name: row.tableName, self: row.self }] : [], false);
	}

	/** Shared open for the reset dialog: every table of the environment can be picked, `selected` preselected (added to the list when the page does not show it). `reloadEnvironment` also re-fetches the environment itself (a reset starts a new lifecycle generation, which retires its repair-needed record). */
	private openResetDialogFor(selected: { name: string; self: string }[], reloadEnvironment: boolean): void {
		if (!this.selectedEnvironment || !this.selectedEnvironmentId) {
			return;
		}
		const tables = this.tableSource.data.map((row) => ({ name: row.tableName, self: row.self }));
		tables.push(...selected.filter((table) => !tables.some((candidate) => candidate.name === table.name)));
		tables.sort((a, b) => a.name.localeCompare(b.name));
		const data: IResetSyncDialogData = {
			environmentId: this.selectedEnvironmentId,
			environmentName: this.selectedEnvironment.name,
			tables,
			selected: selected.map((table) => table.name),
		};

		this.dialog
			.open(ResetSyncDialogComponent, { data, width: '640px' })
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((success) => {
				if (success && this.selectedEnvironmentId) {
					this.notificationService.notify([`${TRANSLATION_NS}.reset.requested`], [], 'success');
					if (reloadEnvironment) {
						this.reloadEnvironment(this.selectedEnvironmentId);
					} else {
						this.loadEnvironmentData(this.selectedEnvironmentId);
					}
				}
			});
	}

	// --- Repair-needed actions (superadmin only) -------------------------------

	/** The repair core-one found for the environment's current generation and left to an operator. */
	get repairNeeded(): IEnvironmentRepairNeeded | null {
		return this.selectedEnvironment?.repairNeeded ?? null;
	}

	/** Whether core-one recommends applying the repair (otherwise resetting the named tables). */
	get isRepairRecommended(): boolean {
		return isRepairRecommended(this.repairNeeded);
	}

	/** Opens the repair dialog, which shows core-one's dry run before applying; reloads the environment on success. */
	openRepairDialog(): void {
		// Superadmin-only mutation — defence-in-depth beyond the *ngIf on the buttons.
		const repairNeeded = this.repairNeeded;
		if (!this.hasSuperadminPermission || !this.selectedEnvironment || !this.selectedEnvironmentId || !repairNeeded) {
			return;
		}
		const data: IRepairEnvironmentDialogData = {
			environmentId: this.selectedEnvironmentId,
			environmentName: this.selectedEnvironment.name,
			repairNeeded,
		};

		this.dialog
			.open(RepairEnvironmentDialogComponent, { data, width: '560px' })
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((success) => {
				if (success && this.selectedEnvironmentId) {
					this.notificationService.notify([`${TRANSLATION_NS}.repair-needed.repaired`], [], 'success');
					this.reloadEnvironment(this.selectedEnvironmentId);
				}
			});
	}

	/** Opens the reset dialog with exactly the tables the repair-needed record names preselected. */
	openRepairNeededResetDialog(): void {
		const repairNeeded = this.repairNeeded;
		if (!this.hasSuperadminPermission || !this.selectedEnvironment || !repairNeeded) {
			return;
		}
		const tables = buildRepairNeededTables(this.selectedEnvironment.self, repairNeeded.tables);
		if (tables.length === 0) {
			this.notify('errors.repair-needed-without-tables');
			return;
		}

		this.openResetDialogFor(tables, true);
	}

	// --- Worker actions (superadmin only) --------------------------------------

	/** Opens the create-worker dialog; reloads the environment (incl. workers) on success. */
	openCreateWorkerDialog(): void {
		this.openWorkerDialog(undefined);
	}

	/** Opens the worker dialog in edit mode, prefilled from the given worker card. */
	openEditWorkerDialog(worker: IWorkerDisplay): void {
		this.openWorkerDialog(worker);
	}

	/** Shared open for the create/edit worker dialog; reloads + notifies on success. */
	private openWorkerDialog(worker: IWorkerDisplay | undefined): void {
		// Superadmin-only mutation — defence-in-depth beyond the *ngIf on the buttons.
		if (!this.hasSuperadminPermission || !this.selectedEnvironment || !this.selectedEnvironmentId) {
			return;
		}
		const data: ICreateWorkerDialogData = {
			environmentId: this.selectedEnvironmentId,
			environmentName: this.selectedEnvironment.name,
			...(worker
				? {
						worker: {
							id: worker.id,
							name: worker.name,
							filters: worker.filters.map((f) => ({
								name: f.name ?? '',
								value: f.value ?? '',
							})),
						},
					}
				: {}),
		};

		this.dialog
			.open(CreateWorkerDialogComponent, { data, width: '520px' })
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((success) => {
				if (success && this.selectedEnvironmentId) {
					this.notificationService.notify([`${TRANSLATION_NS}.create-worker-dialog.${worker ? 'updated' : 'created'}`], [], 'success');
					this.loadEnvironmentData(this.selectedEnvironmentId);
				}
			});
	}

	/** Confirms then deletes a worker; reloads + notifies on success, notifies on failure. */
	confirmDeleteWorker(worker: IWorkerDisplay): void {
		// Superadmin-only mutation — defence-in-depth beyond the *ngIf on the button.
		if (!this.hasSuperadminPermission || !this.selectedEnvironmentId) {
			return;
		}
		this.dialog
			.open(ConfirmdialogComponent, {
				width: '440px',
				data: {
					heading: this.translateService.instant(`${TRANSLATION_NS}.workers.delete-confirm-title`),
					content: this.translateService.instant(`${TRANSLATION_NS}.workers.delete-confirm-content`, { name: worker.name }),
					okButtonText: this.translateService.instant(`${TRANSLATION_NS}.workers.delete-confirm-ok`),
					cancelButtonText: this.translateService.instant('general.cancel'),
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((confirmed) => {
				if (confirmed) {
					this.deleteWorker(worker);
				}
			});
	}

	private deleteWorker(worker: IWorkerDisplay): void {
		const environmentId = this.selectedEnvironmentId;
		if (!environmentId) {
			return;
		}
		this.syncMonitoringService
			.deleteWorker(environmentId, worker.id)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.notificationService.notify([`${TRANSLATION_NS}.workers.deleted`], [], 'success');
					if (this.isCurrentEnvironment(environmentId)) {
						this.loadEnvironmentData(environmentId);
					}
				},
				error: () => this.notify('workers.delete-failed'),
			});
	}

	/** GenericTable row-action handler — opens the reset dialog for the row. `menuEmitter` emits `{ action: actionDefintion, data }`, i.e. `action` is the full action definition object (see `SimpleActionManager.prepareVisibleActions`), not the handler string. */
	handleRowMenuAction(event: unknown): void {
		const { action, data } = event as {
			action: actionDefintion<ITableRow>;
			data: ITableRow;
		};
		if (action.handler === 'reset') {
			this.openResetDialog(data);
		} else if (action.handler === 'audit') {
			this.openDriftAuditDialog([data.tableName]);
		} else if (action.handler === 'findings') {
			this.showAuditFindings(data.tableName);
		}
	}

	/** Per-row CSS classes for GenericTable: status colour + stale + error-navigation highlight. */
	rowClassFn = (row: ITableRow): string[] => {
		const classes = [this.statusClass(row.status)];
		if (row.stale) {
			classes.push('sm-row-stale');
		}
		if (this.highlightedTableSelf && row.self === this.highlightedTableSelf) {
			classes.push('sm-row-highlight');
		}
		return classes;
	};

	// --- Loading orchestration (per-section, independent — R15) ----------------

	/** Generation defaults to a fresh value for callers that load independently of reloadEnvironment() (initial account load, reset-triggered reload); reloadEnvironment() passes its own shared value so refresh's getEnvironment() and this method's requests rise/fall together (see reloadEnvironment). */
	private loadEnvironmentData(environmentId: string, generation = ++this.reloadGeneration): void {
		const isCurrentLoad = (): boolean => generation === this.reloadGeneration && this.isCurrentEnvironment(environmentId);

		this.isTableLoading = true;
		this.isTransitionsLoading = true;
		this.workersError = null;
		this.transitionsError = null;
		this.collapseExpandedRow();

		this.syncMonitoringService
			.getEnvironmentStatus(environmentId)
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => {
					if (isCurrentLoad()) {
						this.notify('errors.status-load-failed');
					}
					return of(null);
				}),
				finalize(() => {
					// Guarded: an out-of-order response for an environment the user has since
					// switched away from (or a superseded overlapping reload) must not clear the
					// loading state for the CURRENT one.
					if (isCurrentLoad()) {
						this.isTableLoading = false;
						this.isLoading = false;
					}
				}),
			)
			.subscribe((status) => {
				if (status && isCurrentLoad()) {
					// statusLastUpdatedAt reflects table/environment data freshness only — set on status success.
					this.statusLastUpdatedAt = new Date();
					this.status = status;
					this.auditTotals = sumAuditTotals(status.tables.map((table) => table.audit));
					this.tableSource.data = this.mapTableRows(status.tables);
					this.applyLinkTable();
				}
			});

		this.syncMonitoringService
			.getWorkers(environmentId)
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => {
					if (isCurrentLoad()) {
						this.workersError = `${TRANSLATION_NS}.errors.workers-load-failed`;
					}
					// null (not []) on failure — an empty array is a legitimate "no workers" success
					// result and must not be conflated with "keep the previously displayed workers" (R13/R14).
					return of(null);
				}),
			)
			.subscribe((workers) => {
				if (workers && isCurrentLoad()) {
					this.enrichWorkers(environmentId, workers, isCurrentLoad);
				}
			});

		this.syncMonitoringService
			.getTransitions(environmentId)
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => {
					if (isCurrentLoad()) {
						this.transitionsError = `${TRANSLATION_NS}.errors.transitions-load-failed`;
					}
					return of(null);
				}),
				finalize(() => {
					if (isCurrentLoad()) {
						this.isTransitionsLoading = false;
					}
				}),
			)
			.subscribe((transitions) => {
				if (transitions && isCurrentLoad()) {
					this.transitions = this.mapTransitions(transitions);
				}
			});

		if (this.activeView === 'errors') {
			this.loadErrors(environmentId);
		}
	}

	/**
	 * Generation defaults to a fresh value per call, so two overlapping errors loads for the same environment (e.g. a refresh while the Errors view is active, or view-activation racing a refresh) can't have an older response overwrite a newer one — isCurrentEnvironment() alone can't distinguish "this call" from "an
	 * earlier call for the same environment".
	 */
	private loadErrors(environmentId: string, generation = ++this.errorsGeneration): void {
		const isCurrentLoad = (): boolean => generation === this.errorsGeneration && this.isCurrentEnvironment(environmentId);

		this.errorsError = null;
		this.syncMonitoringService
			.getErrors(environmentId)
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => {
					if (isCurrentLoad()) {
						this.errorsError = `${TRANSLATION_NS}.errors.errors-load-failed`;
					}
					// null (not []) on failure — an empty array is a legitimate "no errors" success
					// result and must not be conflated with "keep the previously displayed errors" (R13/R14).
					return of(null);
				}),
			)
			.subscribe((errors) => {
				if (errors && isCurrentLoad()) {
					this.errors = this.mapErrors(errors);
				}
			});
	}

	// --- Tables (task .10) -----------------------------------------------------

	onRowExpandToggle(row: ITableRow): void {
		if (this.expandedRow === row) {
			this.collapseExpandedRow();
			return;
		}
		this.expandedRow = row;
		this.loadTableErrors(row);
	}

	private loadTableErrors(row: ITableRow): void {
		const environmentId = this.selectedEnvironmentId;
		if (!environmentId) {
			return;
		}
		this.expandedTableErrors = [];
		this.expandedTableErrorsError = null;
		this.expandedTableErrorsLoading = true;

		// Guarded by row IDENTITY *and* a per-call request id: row identity alone isn't enough — if
		// the user collapses this row and re-expands the SAME row before the first request resolves,
		// `expandedRow === row` would be true again for that stale response. The request id
		// distinguishes "this specific expand" from an earlier expand of the same row. Row identity
		// also covers the environment-switch case, since switching environments resets expandedRow
		// to null (loadEnvironmentData/onEnvironmentChange), so a stale cross-environment response is
		// dropped the same way.
		const requestId = ++this.tableErrorsRequestId;
		const isCurrentRequest = (): boolean => this.expandedRow === row && requestId === this.tableErrorsRequestId;

		this.syncMonitoringService
			.getTableErrors(environmentId, row.tableName)
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => {
					if (isCurrentRequest()) {
						this.expandedTableErrorsError = `${TRANSLATION_NS}.errors.table-errors-load-failed`;
					}
					return of([] as ITableErrorModel[]);
				}),
				finalize(() => {
					if (isCurrentRequest()) {
						this.expandedTableErrorsLoading = false;
					}
				}),
			)
			.subscribe((errors) => {
				if (isCurrentRequest()) {
					this.expandedTableErrors = errors;
				}
			});
	}

	private mapTableRows(tables: IStatusTableModel[]): ITableRow[] {
		const staleDurationUnitLabels = this.getStaleDurationUnitLabels();
		return tables
			.map((table) => this.mapTableRow(table, staleDurationUnitLabels))
			.sort((a, b) => {
				const priority = getStatusSortPriority(a.status) - getStatusSortPriority(b.status);
				return priority !== 0 ? priority : a.tableName.localeCompare(b.tableName);
			});
	}

	private getStaleDurationUnitLabels(): IStaleDurationUnitLabels {
		const ns = `${TRANSLATION_NS}.duration`;
		return {
			day: this.translateService.instant(`${ns}.day`),
			days: this.translateService.instant(`${ns}.days`),
			hour: this.translateService.instant(`${ns}.hour`),
			hours: this.translateService.instant(`${ns}.hours`),
			minute: this.translateService.instant(`${ns}.minute`),
			minutes: this.translateService.instant(`${ns}.minutes`),
			second: this.translateService.instant(`${ns}.second`),
			seconds: this.translateService.instant(`${ns}.seconds`),
		};
	}

	private mapTableRow(table: IStatusTableModel, staleDurationUnitLabels: IStaleDurationUnitLabels): ITableRow {
		const itemSync = table.itemSync;
		const sync = table.synchronization;
		const engine = findEngineTarget(table.targets);
		const staleForMs = itemSync?.staleForMs ?? null;
		const delay = sync?.delay ?? null;
		const sourceDrift = table.sourceDrift?.difference ? table.sourceDrift : null;

		return {
			self: table.self,
			tableName: extractTableName(table.self),
			version: table.version ?? '',
			status: deriveTableStatus(table),
			rowCount: table.rowCount ?? null,
			maxRowVersion: table.maxRowVersion ?? null,
			maxRowVersionHex: this.formatRowVersionHex(table.maxRowVersion ?? null),
			failure: table.failure ?? null,
			processedCount: itemSync?.processedItemCount ?? null,
			queuedCount: itemSync?.queuedItemCount ?? null,
			stage: itemSync?.stage ?? null,
			reason: itemSync?.reason ?? null,
			sourceSyncComplete: itemSync?.sourceSynchronizationComplete ?? null,
			coreProcessingComplete: itemSync?.coreProcessingComplete ?? null,
			stale: itemSync?.stale ?? null,
			staleDuration: staleForMs != null && staleForMs > 0 ? formatStaleDuration(staleForMs, staleDurationUnitLabels) : null,
			targets: table.targets ?? null,
			ignore: sync?.ignore ?? null,
			requisitionId: sync?.requisitionId ?? 0,
			syncRowVersion: sync?.rowVersion ?? null,
			syncRowVersionHex: this.formatRowVersionHex(sync?.rowVersion ?? null),
			lastRequestAt: sync?.lastRequestAt ?? null,
			lastCallbackAt: sync?.lastCallbackAt ?? null,
			delay,
			delayDisplay: delay != null ? formatDelay(delay) : null,
			syncError: sync?.error ?? null,
			engineLabel: this.translateService.instant(`${TRANSLATION_NS}.engine-statuses.${!engine ? 'one-only' : isTargetSynchronized(engine) ? 'synchronized' : (engine.status?.toLowerCase() ?? 'unknown')}`),
			engineRowCount: engine?.targetObservedRowCount ?? null,
			auditLabel: this.auditLabel(table),
			auditIssue: isAuditIssue(table.audit),
			notInEngine: isNotInEngine(engine),
			sourceDrift,
			sourceDriftLabel: this.sourceDriftLabel(sourceDrift?.difference),
			sourceDriftDifference: sourceDrift?.difference ?? 0,
		};
	}

	/** The Source drift column: a warning badge with the difference and its direction, empty without drift. */
	private sourceDriftLabel(difference: number | null | undefined): string {
		const badge = sourceDriftBadge(difference);
		return badge ? `<span class="sm-badge sm-badge-warn">${this.translateService.instant(`${TRANSLATION_NS}.source-drift.${badge.direction}`, { count: badge.count })}</span>` : '';
	}

	/** The Audit column: the audit status, with the drift counts for a differing table. */
	private auditLabel(table: IStatusTableModel): string {
		const audit = table.audit;
		const status = auditStatusOf(audit);
		if (status !== 'differing') {
			return this.translateService.instant(`${TRANSLATION_NS}.audit.statuses.${status}`);
		}
		return this.translateService.instant(`${TRANSLATION_NS}.audit.differing-label`, {
			missing: audit?.missingCount ?? 0,
			mismatch: audit?.mismatchedCount ?? 0,
			exhausted: audit?.exhaustedCount ?? 0,
		});
	}

	// --- Workers (task .11) ----------------------------------------------------

	private enrichWorkers(environmentId: string, workers: IWorkerModel[], isCurrentLoad: () => boolean): void {
		if (workers.length === 0) {
			this.workers = [];
			return;
		}

		const now = Date.now();
		forkJoin(
			workers.map((worker) =>
				this.syncMonitoringService.getWorkerHeartbeat(environmentId, this.extractId(worker.self)).pipe(
					map((heartbeat) => this.buildWorkerDisplay(worker, heartbeat, now)),
					catchError(() => of(this.buildWorkerDisplay(worker, null, now, true))),
				),
			),
		)
			.pipe(takeUntil(this.destroy$))
			.subscribe((displays) => {
				if (isCurrentLoad()) {
					// The heartbeat history's summary (restarts or same process) shows before it is opened.
					displays.filter((worker) => worker.hasHeartbeat).forEach((worker) => this.loadHeartbeatHistory(worker.id, true));
					this.workers = displays.sort((a, b) => getWorkerSortPriority(a) - getWorkerSortPriority(b));
				}
			});
	}

	private buildWorkerDisplay(worker: IWorkerModel, heartbeat: IHeartbeatModel | null, now: number, unavailable = false): IWorkerDisplay {
		const updatedAt = heartbeat?.meta?.updatedAt ?? null; // epoch ms; may be absent even when a heartbeat exists
		if (!heartbeat || updatedAt == null) {
			// heartbeat null => never reported (404); unavailable => non-404 heartbeat error;
			// heartbeat present but no timestamp => treat as "never reported" for display.
			return {
				name: worker.name,
				id: extractWorkerId(worker.self),
				self: worker.self,
				lastHeartbeat: null,
				lastHeartbeatFormatted: '',
				isStale: false,
				hasHeartbeat: false,
				heartbeatUnavailable: unavailable,
				jobAgentVersion: heartbeat?.jobAgentVersion ?? null,
				databaseVersion: heartbeat?.databaseVersion ?? null,
				ipAddress: heartbeat?.ipAddress ?? null,
				hostName: heartbeat?.hostName ?? null,
				dbServerName: heartbeat?.dbServerName ?? null,
				dbName: heartbeat?.dbName ?? null,
				oneConfiguration: heartbeat?.oneConfiguration ?? null,
				filters: (worker.filters ?? []).map((filter) => ({
					name: filter.name ?? null,
					value: filter.value ?? null,
				})),
			};
		}

		const lastHeartbeat = new Date(updatedAt);
		return {
			name: worker.name,
			id: extractWorkerId(worker.self),
			self: worker.self,
			lastHeartbeat,
			lastHeartbeatFormatted: aciesDateFormat(lastHeartbeat, true, true),
			isStale: isHeartbeatStale(updatedAt, now),
			hasHeartbeat: true,
			heartbeatUnavailable: false,
			jobAgentVersion: heartbeat.jobAgentVersion ?? null,
			databaseVersion: heartbeat.databaseVersion ?? null,
			ipAddress: heartbeat.ipAddress ?? null,
			hostName: heartbeat.hostName ?? null,
			dbServerName: heartbeat.dbServerName ?? null,
			dbName: heartbeat.dbName ?? null,
			oneConfiguration: heartbeat.oneConfiguration ?? null,
			filters: (worker.filters ?? []).map((filter) => ({
				name: filter.name ?? null,
				value: filter.value ?? null,
			})),
		};
	}

	/** Loads a worker's last 5 heartbeats; `reload` fetches them again even when they are loaded (each workers refresh does). */
	loadHeartbeatHistory(workerId: string, reload = false): void {
		const environmentId = this.selectedEnvironmentId;
		if (!environmentId || (!reload && this.heartbeatHistory.has(workerId))) {
			return;
		}
		if (!this.heartbeatHistory.has(workerId)) {
			this.heartbeatHistory.set(workerId, 'loading');
		}
		this.syncMonitoringService
			.getWorkerHeartbeats(environmentId, workerId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (heartbeats) => {
					if (this.isCurrentEnvironment(environmentId)) {
						this.heartbeatHistory.set(workerId, this.mapHeartbeats(heartbeats));
					}
				},
				error: () => {
					if (this.isCurrentEnvironment(environmentId)) {
						this.heartbeatHistory.set(workerId, 'failed');
					}
				},
			});
	}

	heartbeatRows(worker: IWorkerDisplay): IHeartbeatRow[] | null {
		const history = this.heartbeatHistory.get(worker.id);
		return Array.isArray(history) ? history : null;
	}

	heartbeatFailed(worker: IWorkerDisplay): boolean {
		return this.heartbeatHistory.get(worker.id) === 'failed';
	}

	heartbeatRestarts(rows: IHeartbeatRow[]): number {
		return rows.filter((row) => row.restarted).length;
	}

	private mapHeartbeats(heartbeats: IHeartbeatModel[]): IHeartbeatRow[] {
		return heartbeats.map((heartbeat, index) => ({
			at: heartbeat.meta?.updatedAt != null ? aciesDateFormat(new Date(heartbeat.meta.updatedAt), true, true) : '—',
			pid: heartbeat.pid ?? null,
			uptime: formatUptime(heartbeat.timeSpentAlive),
			hostName: heartbeat.hostName ?? null,
			jobAgentVersion: heartbeat.jobAgentVersion ?? null,
			restarted: isHeartbeatRestart(heartbeats, index),
		}));
	}

	// --- Errors + transitions (task .12) ---------------------------------------

	navigateToTable(tableSelf: string, failureKind = ''): void {
		// Audit findings live on the Audit tab.
		if (failureKind.startsWith('audit')) {
			this.showAuditFindings(extractTableName(tableSelf));
			return;
		}
		this.activeView = 'tables';
		this.highlightedTableSelf = tableSelf;
		// Scroll to the highlighted row once change detection has applied the class (best-effort).
		setTimeout(() => document.querySelector('.sm-row-highlight')?.scrollIntoView({ block: 'center' }), 0);
		setTimeout(() => {
			if (this.highlightedTableSelf === tableSelf) {
				this.highlightedTableSelf = null;
			}
		}, HIGHLIGHT_CLEAR_MS);
	}

	/** Latest first; errors without a time keep their order after the ones with one. */
	private mapErrors(errors: ITableErrorModel[]): ITableErrorDisplay[] {
		return errors
			.map((error) => {
				const at = error.meta?.updatedAt ?? error.meta?.createdAt ?? null;
				return {
					tableSelf: error.table.self,
					tableName: extractTableName(error.table.self),
					failure: error.failure ?? '',
					failureScope: error.failureScope ?? null,
					failureKind: error.failureKind ?? '',
					isRetriable: error.isRetriable ?? false,
					at,
					atFormatted: at != null ? aciesDateFormat(new Date(at), true, true) : '',
				};
			})
			.sort((a, b) => (b.at ?? Number.NEGATIVE_INFINITY) - (a.at ?? Number.NEGATIVE_INFINITY));
	}

	private mapTransitions(transitions: ITransitionModel[]): ITransitionDisplay[] {
		return [...transitions]
			.sort((a, b) => (b.lastSeen ?? 0) - (a.lastSeen ?? 0))
			.slice(0, TRANSITIONS_LIMIT)
			.map((transition) => {
				const lastSeen = new Date(transition.lastSeen ?? 0);
				return {
					id: transition.id,
					source: transition.source ?? null,
					event: transition.event ?? null,
					action: transition.action ?? null,
					outcome: transition.outcome ?? null,
					previousState: transition.previousState ?? null,
					nextState: transition.nextState ?? null,
					context: transition.context ?? null,
					count: transition.count ?? 0,
					lastSeen,
					lastSeenFormatted: aciesDateFormat(lastSeen, true, true),
				};
			});
	}

	// --- Template helpers ------------------------------------------------------

	truncateSyncError(error: string | null): string {
		return truncateSyncErrorUtil(error);
	}

	/** Pretty-prints a worker's serialized oneConfiguration JSON for the collapsible detail. */
	formatConfiguration(raw: string | null): string {
		return formatWorkerConfiguration(raw);
	}

	isDelayHigh(delay: number | null): boolean {
		return delay != null && delay > DELAY_HIGH_THRESHOLD_SECONDS;
	}

	statusClass(status: string): string {
		return `sm-status-${status}`;
	}

	readonly isTargetSynchronized = isTargetSynchronized;
	readonly hasTargetRowSnapshot = hasTargetRowSnapshot;

	readonly formatEpoch = formatEpoch;
	readonly sourceDriftKey = sourceDriftKey;
	readonly isFoundNotRepaired = isFoundNotRepaired;

	getEnvironmentEngineId(environment: IEnvironmentDetail): string | null {
		const engineId = environment.engineId ?? environment.EngineId ?? environment.engine ?? environment.Engine ?? null;
		return engineId != null ? String(engineId) : null;
	}

	formatRowVersion(rowVersion: number | null): string {
		const hex = this.formatRowVersionHex(rowVersion);
		return rowVersion != null ? `${rowVersion} (${hex})` : '—';
	}

	private formatRowVersionHex(rowVersion: number | null): string | null {
		return rowVersion != null ? `0x${Math.trunc(rowVersion).toString(16).toUpperCase()}` : null;
	}

	// --- State helpers ---------------------------------------------------------

	private resetState(): void {
		this.selectedEnvironment = null;
		this.selectedEnvironmentId = null;
		this.environments = [];
		this.environmentOptions = [];
		this.tableSource.data = [];
		this.workers = [];
		this.errors = [];
		this.transitions = [];
		this.statusLastUpdatedAt = null;
		this.status = null;
		this.auditTotals = null;
		this.heartbeatHistory.clear();
		this.collapseExpandedRow();
		this.highlightedTableSelf = null;
		this.workersError = null;
		this.errorsError = null;
		this.transitionsError = null;
		this.selectorDisabled = false;
		this.defaultEnvironmentSettled = false;
	}

	/**
	 * Guards against out-of-order/stale async responses: true only when `environmentId` is still the one currently selected. Every environment-scoped load (status/workers/errors/transitions/ table-errors) captures its target environmentId at request time and re-checks this before writing to shared component state, so
	 * a slow response for an environment the user has since switched away from can never overwrite the current environment's data (or, worse, be used to target the wrong environment's tables for a synchronization reset).
	 */
	private isCurrentEnvironment(environmentId: string): boolean {
		return environmentId === this.selectedEnvironmentId;
	}

	private mergeSelectedEnvironmentFromList(): void {
		if (!this.selectedEnvironmentId) {
			return;
		}

		const environmentFromList = this.findEnvironmentFromList(this.selectedEnvironmentId);
		if (environmentFromList) {
			this.selectedEnvironment = {
				...environmentFromList,
				...(this.selectedEnvironment ?? {}),
			};
		}
	}

	private findEnvironmentFromList(environmentId: string): IEnvironmentSummary | null {
		return this.environments.find((env) => this.extractId(env.self) === environmentId) ?? null;
	}

	private collapseExpandedRow(): void {
		this.expandedRow = null;
		this.expandedTableErrors = [];
		this.expandedTableErrorsError = null;
		this.expandedTableErrorsLoading = false;
		this.tableErrorsRequestId++;
	}

	/** Same guard as {@link isCurrentEnvironment}, but for account switches (R11): a slow response for an account the user has since switched away from must not overwrite the newer account's already-loaded state. */
	private isCurrentAccount(accountId: string | null): boolean {
		return accountId === this.currentAccountId;
	}

	/** Extracts the last self-link segment — the id used as the core-one path parameter (env or worker). */
	private extractId(self: string): string {
		return self.split('/').filter(Boolean).pop() ?? '';
	}

	private notify(key: string): void {
		this.notificationService.notify([`${TRANSLATION_NS}.${key}`], [], 'error');
	}
}
